import { useState } from "react"
import { KeyedMutator } from "swr"

import {
    SUPPORT_REFER,
    SUPPORT_REFER_HELP_DESK,
} from "../../../../errors/errorMessages"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { snackbar } from "../../../../utils/general/generalUtils"
import { deleteClientProtected } from "../../clients/clients"
import { IDraftApplicationListResponse } from "../../types/installerInterface/installerApplicationTypes"
import { DELETE_DRAFT } from "../../urls/hybrid"
import { useClearHasDrafts } from "../installerInterface/useHasDrafts"

type IDeleteDraft = {
    deleteDraft: () => Promise<void>
    isLoading: boolean
}

/**
 * useDeleteDraft is a custom hook that handles the deletion of a draft.
 * It deletes the draft from the backend and manages the loading state during the process.
 */
const useDeleteDraft = (
    draftId: string, // Draft ID to be deleted
    mutate: KeyedMutator<IDraftApplicationListResponse[]>
): IDeleteDraft => {
    const [isLoading, setIsLoading] = useState(false)
    const clearHasDrafts = useClearHasDrafts()

    const deleteDraft = async (): Promise<void> => {
        try {
            setIsLoading(true)
            // Call the backend function to delete the draft
            await deleteClientProtected(DELETE_DRAFT(draftId))

            snackbar(`Draft ${draftId} successfully deleted`, "success")
            // Mutate to update the list of drafts after deletion
            mutate()
            clearHasDrafts()
        } catch (err) {
            const errorObj = parseErrorAsObj(err)

            let errorMsg
            if (errorObj?.code === 403) {
                errorMsg = `${errorObj?.message}. Please ${SUPPORT_REFER_HELP_DESK}`
            } else {
                errorMsg = `Unable to delete draft ${draftId}. Please try again. ${SUPPORT_REFER}`
            }

            snackbar(errorMsg, "warning")
        } finally {
            setIsLoading(false)
        }
    }

    return {
        deleteDraft,
        isLoading,
    }
}

export default useDeleteDraft
